// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { CheckmarkOutline } from '@carbon/icons-react';
import type { UnboundEquipment } from '../../types/sopGovernance';
import { equipmentTypeLabel } from './formatters';
import { MissingRegion } from '../common/MissingValue';

/** How many gaps to render before collapsing the tail into a count. */
const VISIBLE_LIMIT = 50;

/**
 * Equipment with no bound SOP — the design binding's "Open compliance gaps",
 * read-only.
 *
 * The design gives each gap a remediation button (bind / author / retire), a
 * prose explanation and a suggested SOP with a confidence score. None of those
 * exist upstream: there is no gap resource, no suggestion endpoint, and no
 * write endpoint to bind or retire anything. Rather than render buttons that
 * cannot act, this lists what the twin does say and leaves remediation out
 * until a contract exists for it.
 */
export default function UnboundEquipmentList({ items }: { items: UnboundEquipment[] | null }) {
  // Unmeasured, not empty. The section keeps its heading and its place; only the
  // rows are missing, and the reason sits where they would be. Rendering the
  // all-clear panel here would assert full compliance nobody measured.
  if (items === null) {
    return (
      <MissingRegion
        title="Gaps not measured"
        detail="Which equipment is unbound cannot be listed: the Digital Twin publishes no hasSOP attribute on equipment, so no item can be classed as bound or unbound."
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="sg-all-clear">
        <CheckmarkOutline size={16} />
        <span>Every equipment item in scope has a bound SOP.</span>
      </div>
    );
  }

  const visible = items.slice(0, VISIBLE_LIMIT);
  const hidden = items.length - visible.length;

  return (
    <>
      <ul className="sg-gaps">
        {visible.map(item => (
          <li key={`${item.siteId}:${item.entityId}`} className="sg-gap">
            <div className="sg-gap-body">
              <div className="sg-gap-title">
                <strong>{item.label}</strong>
                <span className="sg-issue sg-issue--open">No bound SOP</span>
                <span className="sg-gap-site">{item.siteLabel}</span>
              </div>
              <p className="sg-gap-detail">
                {equipmentTypeLabel(item.equipmentType)} · {item.entityId}
              </p>
            </div>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <p className="sg-truncated">
          Showing {visible.length} of {items.length}. {hidden} more not listed.
        </p>
      )}
    </>
  );
}
